/**
 * フォーム回答「まとめ」タブ。設問毎に回答を集約表示。
 * v3.1.4
 */
import type {
  FieldAggregation,
  FormSubmission,
} from '@goenhub/shared';
import { aggregateAllFields, extractFieldElements } from '@goenhub/shared';
import { useMemo } from 'react';

import { EmptyState } from '@/components/common/EmptyState';
import { Badge } from '@/components/ui/badge';
import { FileText } from 'lucide-react';

import type { FormRecord } from '@/hooks/useForms';

interface Props {
  form: FormRecord;
  submissions: Array<Pick<FormSubmission, 'values'>>;
}

function fieldTypeLabel(type: string): string {
  const map: Record<string, string> = {
    text: 'テキスト',
    textarea: 'テキストエリア',
    email: 'メール',
    tel: '電話',
    number: '数値',
    select: 'プルダウン',
    radio: 'ラジオ',
    checkbox: 'チェックボックス',
    date: '日付',
    consent: '同意',
    briefing_schedule_picker: '個別説明枠',
    product_picker: '商品',
    assignee_picker: '担当者',
  };
  return map[type] ?? type;
}

function FieldSummaryCard({ agg }: { agg: FieldAggregation }) {
  const isCategorical =
    agg.optionCounts && Object.keys(agg.optionCounts).length > 0;
  const isNumeric = agg.numericValues && agg.numericValues.length > 0;
  const isDate = agg.dateValues && agg.dateValues.length > 0;
  const isText = agg.textValues && agg.textValues.length > 0;

  const responseRate = agg.totalSubmissions
    ? Math.round((agg.answeredCount / agg.totalSubmissions) * 100)
    : 0;

  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-4 space-y-3">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <h4 className="text-sm font-semibold text-[var(--text-primary)]">
            {agg.label || '(無題の設問)'}
          </h4>
          <Badge variant="default">{fieldTypeLabel(agg.fieldType)}</Badge>
        </div>
        <p className="text-xs text-[var(--text-muted)]">
          回答 {agg.answeredCount} / {agg.totalSubmissions} 件 ({responseRate}%)
        </p>
      </div>

      {isCategorical && (
        <div className="space-y-1.5">
          {Object.entries(agg.optionCounts!)
            .sort((a, b) => b[1] - a[1])
            .map(([opt, count]) => {
              const pct = agg.answeredCount
                ? Math.round((count / agg.answeredCount) * 100)
                : 0;
              return (
                <div key={opt} className="text-xs">
                  <div className="flex justify-between mb-0.5">
                    <span className="text-[var(--text-primary)]">{opt}</span>
                    <span className="text-[var(--text-muted)] tabular-nums">
                      {count} 件 ({pct}%)
                    </span>
                  </div>
                  <div className="h-1.5 bg-[var(--bg-elevated)] rounded-sm overflow-hidden">
                    <div
                      className="h-full bg-[var(--accent)]"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
        </div>
      )}

      {isNumeric && (
        <div className="text-xs text-[var(--text-muted)] space-y-1">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-wider">平均</div>
              <div className="text-sm tabular-nums text-[var(--text-primary)]">
                {(
                  agg.numericValues!.reduce((a, b) => a + b, 0) /
                  agg.numericValues!.length
                ).toFixed(1)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider">最小</div>
              <div className="text-sm tabular-nums text-[var(--text-primary)]">
                {Math.min(...agg.numericValues!)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider">最大</div>
              <div className="text-sm tabular-nums text-[var(--text-primary)]">
                {Math.max(...agg.numericValues!)}
              </div>
            </div>
          </div>
          <details>
            <summary className="cursor-pointer hover:text-[var(--text-primary)]">
              ▸ 全回答 ({agg.numericValues!.length} 件)
            </summary>
            <div className="mt-1 text-[var(--text-secondary)] flex flex-wrap gap-1.5">
              {agg.numericValues!.map((v, i) => (
                <code
                  key={i}
                  className="bg-[var(--bg-elevated)] px-1.5 py-0.5 rounded-sm text-[10px] font-mono"
                >
                  {v}
                </code>
              ))}
            </div>
          </details>
        </div>
      )}

      {isDate && (
        <details className="text-xs">
          <summary className="cursor-pointer text-[var(--text-muted)] hover:text-[var(--text-primary)]">
            ▸ 全回答 ({agg.dateValues!.length} 件)
          </summary>
          <div className="mt-1 text-[var(--text-secondary)] flex flex-wrap gap-1.5">
            {agg.dateValues!.map((v, i) => (
              <code
                key={i}
                className="bg-[var(--bg-elevated)] px-1.5 py-0.5 rounded-sm text-[10px] font-mono"
              >
                {v}
              </code>
            ))}
          </div>
        </details>
      )}

      {isText && (
        <div className="text-xs text-[var(--text-secondary)] space-y-1.5">
          {agg.textValues!.slice(0, 50).map((v, i) => (
            <div
              key={i}
              className="px-2 py-1 bg-[var(--bg-elevated)] rounded-sm border border-[var(--border)] whitespace-pre-wrap break-words"
            >
              {v}
            </div>
          ))}
          {agg.textValues!.length > 50 && (
            <div className="text-[var(--text-muted)] italic">
              … 残り {agg.textValues!.length - 50} 件は CSV エクスポートで確認
            </div>
          )}
        </div>
      )}

      {!isCategorical && !isNumeric && !isDate && !isText && (
        <p className="text-xs text-[var(--text-muted)] italic">
          まだ回答がありません。
        </p>
      )}
    </div>
  );
}

export function SubmissionsSummaryTab({ form, submissions }: Props) {
  const aggregations = useMemo(
    () => aggregateAllFields(form.elements, submissions, { includeText: true }),
    [form.elements, submissions],
  );

  const fieldElements = extractFieldElements(form.elements);
  if (fieldElements.length === 0) {
    return (
      <EmptyState
        icon={FileText}
        title="設問が定義されていません"
        description="ビルダータブから設問を追加してください。"
      />
    );
  }

  if (submissions.length === 0) {
    return (
      <EmptyState
        icon={FileText}
        title="回答がまだありません"
        description="送信があると、ここに設問ごとの集計が表示されます。"
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="text-xs text-[var(--text-muted)] mb-2">
        全 <span className="text-[var(--text-primary)] font-medium">{submissions.length}</span> 件の回答を、設問単位で集計しています。
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {aggregations.map((agg) => (
          <FieldSummaryCard key={agg.fieldId} agg={agg} />
        ))}
      </div>
    </div>
  );
}
